/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import Component from 'can/component/component';
import _ from 'lodash';

import Map from 'scripts/models/base/map';
import FeedService from 'scripts/services/base-feed-service';
import appContext from 'scripts/app-context';
import RouteCreator from 'scripts/services/route-creator';
import deviceUtil from 'scripts/utils/device';
import liveVideoService from 'scripts/services/live-video-service';
import dtm from 'scripts/services/dtm';
import { showLiveVideoModal } from 'components/live-video-modal/live-video-modal';

import {
    WIN_PROBABILITY,
    MAKE_CUT_PROBABILITY,
    TOP_10_PROBABILITY,
} from '../../probability/types';

import videoModalDialog from './video-modal-dialog.stache';
import template from './drawer.stache';
import { ProbabilityStateService } from 'scripts/services/probability-state.service';
import { PROBABILITY_PAGE_TYPE } from 'components/probability/tooltip-handler';

const playerFeedUrl = window.globalSettings.feeds.smt.playerUrl;
const inWebView = window.globalSettings.inWebView;

const VIEW_TYPES = {
    MOBILE: 'MOBILE',
    PORTRAIT: 'PORTRAIT',
    DESKTOP: 'DESKTOP',
};

const DESKTOP_TABS = ['stats', 'media'];
const TABLET_TABS = ['scorecard', 'stats', 'media'];
const MOBILE_TABS = [
    'scorecard',
    'shot-by-shot',
    'probability',
    'stats',
    'media',
];

const getDataTypeSelection = (view, value) => {
    let list = MOBILE_TABS;

    if (view === VIEW_TYPES.DESKTOP) {
        list = DESKTOP_TABS;
    }

    if (view === VIEW_TYPES.PORTRAIT) {
        list = TABLET_TABS;
    }

    if (!Boolean(value) || !list.includes(value)) {
        return list[0];
    }

    return value;
};
export const DRAWER_TYPES = {
    LEADERBOARD: 'LEADERBOARD',
    LIVE: 'LIVE',
};

Component.extend({
    tag: 'leaderboard-drawer',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            isMobile: {
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            DRAWER_TYPES: {
                type: 'object',
                value: DRAWER_TYPES,
            },
            showDropdown: {
                value: true,
            },
            type: {
                type: 'string',
            },
            pageType: {
                get() {
                    const drawerType = this.attr('type');

                    if (drawerType === DRAWER_TYPES.LIVE) {
                        return PROBABILITY_PAGE_TYPE.LIVE_VIDEO;
                    }

                    return PROBABILITY_PAGE_TYPE.LEADERBOARD;
                },
            },
            showLeaders: {
                get() {
                    return this.attr('type') === DRAWER_TYPES.LEADERBOARD;
                },
            },
            showHeader: {
                get() {
                    return this.attr('type') === DRAWER_TYPES.LEADERBOARD;
                },
            },
            showFullStats: {
                get() {
                    return this.attr('type') === DRAWER_TYPES.LEADERBOARD;
                },
            },
            showProbabilityVideo: {
                get() {
                    return this.attr('type') === DRAWER_TYPES.LEADERBOARD;
                },
            },
            showScorecardVideo: {
                get() {
                    return this.attr('type') === DRAWER_TYPES.LEADERBOARD;
                },
            },
            round: {
                type: 'number',
                value: null,
            },
            data: {
                Type: Map,
                Value: Map,
            },
            sponsor: {
                Type: Map,
                Value: Map,
            },
            player: {
                Type: can.Map,
                value: null,
            },
            isSelected: {
                type: 'boolean',
                value: false,
            },
            disableSelectButton: {
                type: 'boolean',
                value: true,
            },
            dataType: {
                set(value) {
                    this.attr('selectedDataType', value);

                    return value;
                },
            },
            // TODO: investigate, if it's can be removed (dataType duplicate)
            selectedDataType: {
                type: 'string',
                value: null,
            },
            playerPagePath: {
                get() {
                    const playerId = this.attr('data.player.identifier');
                    return RouteCreator.playerDetail(playerId);
                },
            },
            playerFullVideoRecapPath: {
                get() {
                    const isRecapAvailable = this.attr('data.isRecapAvailable');
                    const playerId = this.attr('data.player.identifier');
                    if (isRecapAvailable) {
                        return RouteCreator.playerFullVideoRecap(playerId);
                    }
                    return null;
                },
            },

            getParChart: {
                get() {
                    const round = this.attr('round');

                    if (round) {
                        return this.attr(
                            `player.roundsInfo.${round - 1}.toParChart`,
                        );
                    }

                    return null;
                },
            },
            toParChartMinimum: {
                get() {
                    return _.get(this.attr('getParChart'), 'minimumValue');
                },
            },
            toParChartMaximum: {
                get() {
                    return _.get(this.attr('getParChart'), 'maximumValue');
                },
            },
            toParChart: {
                get() {
                    return _.get(this.attr('getParChart'), 'sections.0.values');
                },
            },
            overallToParChart: {
                get() {
                    const round = this.attr('round');
                    if (round) {
                        return this.attr(
                            `player.overallToParChart.sections.${round -
                                1}.values`,
                        );
                    }
                },
            },
            startHole: {
                get() {
                    const round = this.attr('round');
                    let start = 1;
                    if (round) {
                        start = this.attr(
                            `player.roundsInfo.${round -
                                1}.scorecard.holeResults.0.hole.identifier`,
                        );
                    }
                    return start;
                },
            },
            activeVideo: {
                Type: can.Map,
                value: null,
            },
            liveVideoUrl: {
                type: 'string',
                get() {
                    return this.player.liveVideo
                        ? liveVideoService.getLiveUrl(
                              this.player.liveVideo.identifier,
                          )
                        : '#';
                },
            },
            viewType: {
                type: 'string',
                value: null,
            },
            isSelectable: {
                get() {
                    const positionValue = this.attr(
                        'player.position.displayValue',
                    );

                    if (!positionValue) {
                        return false;
                    }

                    return (
                        positionValue.startsWith('T') ||
                        Boolean(Number(positionValue))
                    );
                },
            },
            noData: {
                type: 'boolean',
                value: false,
            },
            navigationContentTypes: {
                get() {
                    const subTypes =
                        this.attr('round') > 2
                            ? [WIN_PROBABILITY, TOP_10_PROBABILITY]
                            : [
                                  WIN_PROBABILITY,
                                  TOP_10_PROBABILITY,
                                  MAKE_CUT_PROBABILITY,
                              ];

                    return [
                        {
                            type: 'stats',
                            title: 'probability',
                            subTypes,
                        },
                        ...(this.attr('type') === DRAWER_TYPES.LIVE
                            ? []
                            : [
                                  {
                                      type: 'media',
                                      title: 'latest',
                                  },
                              ]),
                    ];
                },
            },
            dayNumber: {
                get() {
                    return _.get(this.player, 'probability.dayNumber');
                },
            },
            defaultSubNavOpen: {
                get() {
                    return this.attr('type') === DRAWER_TYPES.LIVE;
                },
            },
            probabilityType: {
                set(probabilityType) {
                    if (this.attr('dataType') === 'media') {
                        return '';
                    }

                    if (!probabilityType) {
                        return this.getDefaultProbabilityType();
                    }

                    return probabilityType;
                },
            },
            isProbabilityExpanded: {
                type: 'boolean',
                value: false,
            },
            isDisabledTouchEvents: {
                type: 'boolean',
                value: false,
            },
        },
        selectedContentClass:
            'is-selected is-selected-desktop is-selected-tablet is-selected-mobile',
        setSelected(value) {
            this.attr('isSelected', value);
        },
        enableSelectButton() {
            this.attr('disableSelectButton', false);
        },
        watchLiveVideo: function(event, identifier) {
            if (deviceUtil.isMobileWeb) {
                event.preventDefault();
                showLiveVideoModal();
            } else if (deviceUtil.isMobile && !inWebView) {
                event.preventDefault();
                const player = this.attr('data.player');

                appContext.model.dispatch('leaderboard-live-video-play', [
                    identifier,
                    `${player.firstName} ${player.lastName}`,
                ]);
            }
        },
        shotDataIsAvailable() {
            let round = this.attr('round');
            let holeResults = this.attr(
                `player.roundsInfo.${round - 1}.scorecard.holeResults`,
            );
            return !!_.find(holeResults, (hole) => {
                return hole && hole.score.displayValue;
            });
        },
        getDefaultProbabilityType() {
            const day = _.get(this.player, 'probability.dayNumber');

            return day > 2 ? WIN_PROBABILITY : MAKE_CUT_PROBABILITY;
        },
        onNavigationTypeSelect(_context, _el, _event, type, subType) {
            this.changeNavigationType(type, subType);
        },
        onProbabilityTypeChange(_context, _el, _event, type) {
            if (!type) {
                return;
            }

            this.attr('viewType') === VIEW_TYPES.MOBILE
                ? this.changeNavigationType('probability', type)
                : this.changeNavigationType('stats', type);
        },
        changeNavigationType(type, subType) {
            this.attr('dataType', type);
            this.attr('probabilityType', subType);
        },
        toggleProbabilityExpand() {
            this.attr(
                'isProbabilityExpanded',
                !this.attr('isProbabilityExpanded'),
            );
        },
    },
    events: {
        inserted() {
            this.registerListeners();
            this.adaptView(appContext.model.screen.screenSize);
            this.registerFeedService();
            this.fetchSponsor();
        },

        registerDrawerStateBindings() {
            this.viewModel.bind('data', (_, data) => {
                if (!this.playerFeedService) {
                    return;
                }

                this.playerFeedService.fetch({
                    identifier: data.player.identifier,
                });
            });
            this.viewModel.bind('isProbabilityExpanded', (_, isExpanded) => {
                if (!this.stateService) {
                    return;
                }

                this.stateService.setState(
                    this.viewModel.attr('type'),
                    this.viewModel.attr('dayNumber'),
                    {
                        isExpanded,
                    },
                );
            });
            this.viewModel.bind('round', (_, round) => {
                this.updateProbability(round);

                if (!this.stateService) {
                    return;
                }

                this.stateService.setState(
                    this.viewModel.attr('type'),
                    this.viewModel.attr('dayNumber'),
                    {
                        round,
                    },
                );
            });
            this.viewModel.bind('dataType', (_, dataType) => {
                if (!this.stateService) {
                    return;
                }

                this.stateService.setState(
                    this.viewModel.attr('type'),
                    this.viewModel.attr('dayNumber'),
                    {
                        dataType,
                    },
                );
            });
            this.viewModel.bind('probabilityType', (_, type) => {
                if (!this.stateService) {
                    return;
                }

                this.stateService.setState(
                    this.viewModel.attr('type'),
                    this.viewModel.attr('dayNumber'),
                    {
                        type,
                    },
                );
            });
        },

        processRoundAssignment(round) {
            const roundsLength = this.viewModel.attr(
                'player.roundsInfo.length',
            );

            // select last round with data by default or select last round if the number of rounds decreased
            if (roundsLength < round) {
                this.stateService.setState(
                    this.viewModel.attr('type'),
                    this.viewModel.attr('dayNumber'),
                    { round: roundsLength },
                );
                return;
            }

            this.viewModel.attr('round', round);
        },

        initState() {
            this.stateService = new ProbabilityStateService();
            const pageState = this.stateService.getPageState(
                this.viewModel.attr('type'),
            );

            if (!pageState) {
                return;
            }

            const dayState = this.stateService.getDayState(
                this.viewModel.attr('type'),
                this.viewModel.attr('dayNumber'),
            );

            if (!dayState) {
                // remove this line if we want to keep state for each day for each champ
                return this.stateService.flushState(
                    this.viewModel.attr('type'),
                );
            }

            const { isExpanded, dataType, round, type } = dayState;

            if (isExpanded) {
                this.viewModel.attr('isProbabilityExpanded', isExpanded);
            }

            if (round !== undefined) {
                this.processRoundAssignment(round);
            }

            if (dataType) {
                this.viewModel.attr('dataType', dataType);
            }

            if (type) {
                this.viewModel.attr('probabilityType', type);
            }
        },

        registerFeedService() {
            const identifier = this.viewModel.data.player.identifier;
            if (identifier && !this.playerFeedService) {
                this.playerFeedService = new FeedService({
                    feedUrl: playerFeedUrl,
                    fromCache: false,
                });
                this.playerFeedService
                    .on('fetching', (ev, value) =>
                        this.viewModel.attr('fetching', value),
                    )
                    .on('error', () => {
                        this.viewModel.attr('fetching', false);
                        this.viewModel.attr('noData', true);
                    })
                    .on('data', (ev, data) => {
                        this.viewModel.attr('player', data);
                        this.viewModel.attr('noData', false);

                        this.registerDrawerStateBindings();
                        this.updateProbability(this.viewModel.attr('round'));

                        this.initState();
                    });

                this.playerFeedService.fetch({ identifier });
            }
        },

        updateProbability(round) {
            const probabilityType = this.viewModel.attr('probabilityType');

            if (round > 2 && probabilityType === MAKE_CUT_PROBABILITY) {
                this.viewModel.attr('probabilityType', WIN_PROBABILITY);
            }
        },

        registerListeners() {
            this.viewModel.bind('viewType', (_, value) => {
                const dataType = this.viewModel.attr('dataType');
                const dataTypeSelection = getDataTypeSelection(value, dataType);

                this.viewModel.changeNavigationType(
                    dataTypeSelection,
                    dataTypeSelection === 'stats'
                        ? this.viewModel.attr('probabilityType')
                        : null,
                );
            });
            this.viewModel.bind('activeVideo', (ev, video) => {
                let playerPagePath = this.viewModel.attr('playerPagePath');
                window.open(
                    `${playerPagePath}#!&video=${video.identifier}&r=${video.round}&h=${video.hole}`,
                    '_self',
                );
            });
            appContext.model.screen.bind('screenSize', (event, screenSize) => {
                if (screenSize) {
                    this.adaptView(screenSize);
                }
            });
            appContext.model.on(
                'highlights-video-going-play',
                this.openVideoModal,
            );

            appContext.model.bind(
                'probability-swiper-dragging',
                (event, value) => {
                    this.viewModel.attr('isDisabledTouchEvents', value);
                },
            );
        },

        adaptView(sceenSize) {
            switch (sceenSize) {
                case 'extra-large':
                case 'large':
                    this.viewModel.attr('viewType', VIEW_TYPES.DESKTOP);
                    break;
                case 'medium':
                case 'small':
                    this.viewModel.attr('isProbabilityExpanded', false);
                    this.viewModel.attr('viewType', VIEW_TYPES.PORTRAIT);
                    break;
                default:
                    this.viewModel.attr('isProbabilityExpanded', false);
                    this.viewModel.attr('viewType', VIEW_TYPES.MOBILE);
            }
        },

        fetchSponsor() {
            let url = window.globalSettings.feeds.settings;
            this.feedService = new FeedService({
                feedUrl: url,
                fromCache: false,
            });
            this.feedService.fetch();
            this.feedService.on('data', (ev, data) => {
                this.viewModel.attr(
                    'sponsor',
                    _.get(data, 'leaderboardSettings.drawerSponsor'),
                );
            });
        },

        openVideoModal(event, video) {
            if (video) {
                dtm.trackVideoLaunch(
                    video.title,
                    'Leader Board Drawer Scorecard',
                );
                const isIOSModern = deviceUtil.iosVersion >= 12;
                const videoModalElement = videoModalDialog({
                    videoId: video.identifier,
                    onChangeFullscreen(viewModel) {},
                    pauseOnExitFullscreen: isIOSModern,
                });
                $('body').append(videoModalElement);
            }
        },

        removed() {
            if (this.playerFeedService) {
                this.playerFeedService.destroy();
                this.playerFeedService = null;
            }
            appContext.model.unbind(
                'highlights-video-going-play',
                this.openVideoModal,
            );
        },

        '.btn-content-selection click'($el) {
            const dataType = $el.data('type');
            this.viewModel.changeNavigationType(dataType, null);

            appContext.model.dispatch('drawer-content-tabs-toggle', [dataType]);
        },

        '.player__button-select click'(element, event) {
            event.stopPropagation();

            const playerId = this.viewModel.attr('data.player.identifier');
            this.viewModel.dispatch('selectPlayer', [playerId]);
        },

        '.js-drawer-close click'() {
            this.viewModel.dispatch({ type: 'onClose' });
        },
    },
});
